import React, {
  ChangeEvent,
  ClipboardEvent,
  Dispatch,
  Fragment,
  KeyboardEvent,
  SetStateAction,
  useEffect,
  useRef,
  useState
} from 'react';
import Spinner from '../spinner/Spinner';
import { UseMutateAsyncFunction } from '@tanstack/react-query';
import { IValidateOtp } from '../../domain/services/IConnectApi';
import { useHistory } from 'react-router';

interface ISplitInputOtpProps {
  isValidating: boolean;
  setOtpCustomError: Dispatch<SetStateAction<string | undefined>>;
  setBtnState: Dispatch<SetStateAction<{ disabled: boolean; resetCount: number }>>;
  otpCustomError: string | undefined;
  validateOtp: UseMutateAsyncFunction<IValidateOtp, Error, string, unknown>;
}

const OTP_LENGTH = 6;

const SplitInputOtp = (props: ISplitInputOtpProps) => {
  const { isValidating, otpCustomError, setOtpCustomError, validateOtp, setBtnState } = props;
  const [otp, setOtp] = useState(Array<string>(OTP_LENGTH).fill(''));
  const history = useHistory()
  const inputsRef = useRef<Array<HTMLInputElement | null>>([]);
  useEffect(() => {
    if (inputsRef.current) inputsRef.current[0]?.focus();
  }, []);

  const otpCompleted = otp.filter(Boolean).length === OTP_LENGTH;

  useEffect(() => {
    if (otpCompleted) {
      (async () => {
        if (otpCustomError) setOtpCustomError('');
        const data = await validateOtp(otp.join(''));
        const { status } = data || {};

        if(status === "Verified"){
          history.push('/tu-end-success');
          return
        }
        if (status === 'Failed') {
          setOtpCustomError('We were unable to verify your identity with the code provided.');
          setBtnState(prev => ({ ...prev, disabled: false }));
          inputsRef.current?.forEach(el => el && (el.disabled = true));
          return;
        } else if (status === 'Retry') {
          setOtpCustomError('The code entered was incorrect. Please try again.');
        } else if (status === 'Expired') {
          setOtpCustomError('The code entered has expired. Please try again.');
        }
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [otpCompleted]);

  const updateOtp = (startIdx: number, newValues: string) => {
    const newOtp = [...otp];
    for (let i = 0; i < OTP_LENGTH - startIdx && i < newValues.length; i++) {
      newOtp[startIdx + i] = newValues[i];
    }
    setOtp(newOtp);

    const nextIndex = Math.min(startIdx + newValues.length, OTP_LENGTH - 1);
    if (nextIndex < OTP_LENGTH) inputsRef.current[nextIndex]?.focus();
  };

  const onChange = (e: ChangeEvent<HTMLInputElement>, index: number) => {
    const val = e.target.value.replace(/\D/g, '');
    if (!val) return;

    if (val.length <= 1) updateOtp(index, val);
  };

  const onPaste = (e: ClipboardEvent<HTMLInputElement>, index = 0) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('Text').replace(/\D/g, '');
    if (!pasted) return;
    updateOtp(index, pasted);
  };

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>, index: number) => {
    if (e.key === 'Backspace') {
      if (otp[index]) {
        const newOtp = [...otp];
        newOtp[index] = '';
        setOtp(newOtp);
      } else if (index > 0) {
        inputsRef.current[index - 1]?.focus();
      }
    }

    if (e.key === 'ArrowLeft' && index > 0) {
      inputsRef.current[index - 1]?.focus();
    }
    if (e.key === 'ArrowRight' && index < OTP_LENGTH - 1) {
      inputsRef.current[index + 1]?.focus();
    }
  };
  return (
    <form autoComplete="one-time-code" aria-labelledby="otp-label" className="flex gap-2 mt-6 items-center">
      {otp.map((digit, idx) => {
        return (
          <Fragment key={idx}>
            <label htmlFor={`otp-${idx}`} tabIndex={-1}>
              <span className="sr-only">
                Enter code digit {idx + 1} of {OTP_LENGTH}
              </span>
              <input
                className="sm:w-11 sm:h-12 w-9 h-9 text-center text-md border-solid border border-gray-300 rounded-md"
                id={`otp-${idx}`}
                name={`otp-${idx}`}
                required={true}
                ref={el => (inputsRef.current[idx] = el)}
                type="text"
                inputMode="numeric"
                pattern="\d{1}"
                autoComplete={idx === 0 ? 'one-time-code' : 'off'}
                value={digit}
                onChange={e => onChange(e, idx)}
                onKeyDown={e => onKeyDown(e, idx)}
                onPaste={e => onPaste(e, idx)}
              />
            </label>
            {idx === 2 && <span aria-hidden="true" className="w-2 h-[1px] bg-gray-300" />}
          </Fragment>
        );
      })}
      {isValidating && (
        <div className="sm:w-11 sm:h-11 w-8 h-8 sm:ml-4 ml-2">
          <Spinner />
        </div>
      )}
    </form>
  );
};

export default SplitInputOtp;
